import debounce from 'lodash/debounce';
import { useEffect, useMemo, useRef } from 'react';

type HorizontalAttemptSource = 'wheel' | 'touch';

type AttemptInfo = { source: HorizontalAttemptSource; deltaX: number };

type UseHorizontalScrollAttemptOptions = {
  target?: EventTarget;
  threshold?: number;
  touchThreshold?: number;
  debounceMs?: number;

  /**
   * Provide deps so the debounced callback can be recreated with fresh closures.
   * Example: attemptDeps: [isOpen, currentIndex]
   */
  attemptDeps?: React.DependencyList;

  onAttempt: (info: AttemptInfo) => void;
};

export function useHorizontalScrollAttempt({
  target,
  threshold = 12,
  touchThreshold = 12,
  debounceMs = 600,
  attemptDeps = [],
  onAttempt,
}: UseHorizontalScrollAttemptOptions): void {
  const thresholdRef = useRef(threshold);
  const touchThresholdRef = useRef(touchThreshold);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    thresholdRef.current = threshold;
  }, [threshold]);

  useEffect(() => {
    touchThresholdRef.current = touchThreshold;
  }, [touchThreshold]);

  const debouncedAttempt = useMemo(() => {
    const debounced = debounce(
      (info: AttemptInfo) => {
        onAttempt(info);
      },
      debounceMs,
      { leading: true, trailing: false }
    );

    return debounced;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceMs, onAttempt, ...attemptDeps]);

  useEffect(() => {
    return () => {
      debouncedAttempt.cancel();
    };
  }, [debouncedAttempt]);

  useEffect(() => {
    const eventTarget: EventTarget = target ?? window;

    const handleWheel = (event: Event) => {
      if (!(event instanceof WheelEvent)) {
        return;
      }

      const inferredDeltaX =
        event.deltaX !== 0 ? event.deltaX : event.shiftKey ? event.deltaY : 0;

      if (Math.abs(inferredDeltaX) < thresholdRef.current) {
        return;
      }

      debouncedAttempt({ source: 'wheel', deltaX: inferredDeltaX });
    };

    const handleTouchStart = (event: Event) => {
      if (!(event instanceof TouchEvent)) {
        return;
      }

      const touch = event.touches[0];
      if (!touch) {
        return;
      }

      touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    };

    const handleTouchMove = (event: Event) => {
      if (!(event instanceof TouchEvent)) {
        return;
      }

      const touch = event.touches[0];
      const start = touchStartRef.current;

      if (!touch || !start) {
        return;
      }

      const deltaX = touch.clientX - start.x;
      const deltaY = touch.clientY - start.y;

      if (
        Math.abs(deltaX) >= touchThresholdRef.current &&
        Math.abs(deltaX) > Math.abs(deltaY)
      ) {
        debouncedAttempt({ source: 'touch', deltaX });
        touchStartRef.current = { x: touch.clientX, y: touch.clientY };
      }
    };

    eventTarget.addEventListener('wheel', handleWheel, {
      passive: true,
    });

    eventTarget.addEventListener('touchstart', handleTouchStart, {
      passive: true,
    });

    eventTarget.addEventListener('touchmove', handleTouchMove, {
      passive: true,
    });

    return () => {
      eventTarget.removeEventListener('wheel', handleWheel);
      eventTarget.removeEventListener('touchstart', handleTouchStart);
      eventTarget.removeEventListener('touchmove', handleTouchMove);
    };
  }, [target, debouncedAttempt]);
}
